import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-20 h-14 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">{article.date}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-800 mb-3">
            {article.category}
          </span>
          <h3 className="text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 mt-2 line-clamp-2 leading-relaxed">
            {article.summary}
          </p>
          <div className="flex items-center gap-3 mt-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
    >
      <div className="w-full sm:w-48 h-36 sm:h-28 flex-shrink-0 rounded-lg overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-800">
            {article.category}
          </span>
          <span className="text-xs text-foreground-500">{article.date}</span>
        </div>
        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-1.5 line-clamp-2 leading-relaxed">
          {article.summary}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {article.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded text-xs bg-background-200 text-foreground-600"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}